import { MutableRefObject, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { emitFocusFormInput } from '../../events';
import { getNameFromId, useGetIdFromName } from '../../utils';

type UpdateSequenceNumbers = (startIndex: number, endIndex?: number) => void;

export const useUpdateSequenceNumbersRef = (
  tableName: string,
  columnName = 'sequenceNumber'
): MutableRefObject<UpdateSequenceNumbers> => {
  const { getValues, setValue } = useFormContext();
  const getIdFromName = useGetIdFromName();
  const updateSequenceNumbersRef = useRef<UpdateSequenceNumbers>(noop);

  useEffect(() => {
    updateSequenceNumbersRef.current = (
      startIndex: number,
      endIndex?: number
    ) => {
      const values = getValues(tableName);

      if (!Array.isArray(values)) {
        throw new Error(`Expecting "${tableName}" field to hold an array`);
      }
      if (values.length === 0) {
        return;
      }
      if (!endIndex) {
        endIndex = values.length - 1;
      }

      const fields = Array.from(values);

      for (let i = startIndex; i <= endIndex; i++) {
        const rowData = fields[i];
        if (rowData) {
          rowData[columnName] = i + 1;
        }
      }

      const tableElement = document.getElementById(getIdFromName(tableName));
      const restoreFocusElement = tableElement?.contains(document.activeElement)
        ? document.activeElement
        : undefined;

      fields.sort(
        (a, b) =>
          ((a as any)[columnName] || Infinity) -
          ((b as any)[columnName] || Infinity)
      );

      setValue(tableName, fields);

      const tablePath = tableName.split('.');
      if (tablePath.length > 1) {
        setValue(tablePath[0], getValues(tablePath[0])); // Trigger re-render
      }

      if (restoreFocusElement) {
        const restoreFocusElementName =
          restoreFocusElement.getAttribute('name') ||
          getNameFromId(restoreFocusElement.id);

        if (restoreFocusElementName) {
          setTimeout(() => {
            emitFocusFormInput(restoreFocusElementName);
          });
        }
      }
    };
  }, [tableName, columnName, getValues, setValue]);

  return updateSequenceNumbersRef;
};

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = () => {};
